<template>
  <div class="w-full max-h-screen bg-background flex flex-col relative">
    <page-header title="Products" >
      <AppButton
          color="green"
          label="Create New Product"
          @click="openCreateProductPanel"
          :icon="true"
          class="mr-2"
      >
        <template v-slot:icon>
          <Icon iconType="add" />
        </template>
      </AppButton>
    </page-header>

    <div class="p-6 bg-background flex-1 overflow-y-scroll">
      <div class="w-full bg-white p-5 rounded shadow-md relative">
        <!-- Toolbar -->
        <div class="w-full flex items-center justify-between">
          <div class="flex -mx-0 mr-2 w-1/2">
            <TabHeaders :tabs="tabs" :selectedTab="currentTab" @tab-selected="onTabSelected" />
          </div>

          <div class="flex justify-end w-1/2">
            <input
                type="text"
                class="ml-6 w-6/12 rounded-l bge-input bge-input-spacing"
                placeholder="Search"
                v-model="searchTextInput"
                @keydown.enter="triggerSearch"
            />
            <button
                @click="triggerSearch"
                class="rounded-r bg-blue-200 hover:bg-blue-300 border border-blue-400 hover:border-blue-500 p-2 hover:shadow active:shadow-inner transition-colors duration-300 ease-in-out"
            >
              <Icon iconType="search" />
            </button>
            <SearchTipsInfo />
          </div>
        </div>

        <!-- GAP + View Filters (only products + cleaned) -->
        <div v-if="showViewFilters" class="mt-4 mb-4">
          <div class="flex items-center">
            <span class="text-sm text-secondary font-semibold mr-3">View:</span>

            <!-- Segmented control -->
            <div class="inline-flex items-center bg-gray-100 rounded-lg p-1 space-x-1">
              <!-- Products tab: Active / Deleted / Archived / Moved to Cleaned -->
              <template v-if="currentTab === 'products'">
                <button
                    type="button"
                    :class="segBtnClass(productsViewInput === 'active')"
                    @click="setProductsView('active')"
                >
                  Active
                </button>

                <button
                    type="button"
                    :class="segBtnClass(productsViewInput === 'deleted')"
                    @click="setProductsView('deleted')"
                >
                  Deleted
                </button>

                <button
                    type="button"
                    :class="segBtnClass(productsViewInput === 'archived')"
                    @click="setProductsView('archived')"
                >
                  Archived
                </button>

                <button
                    type="button"
                    :class="segBtnClass(productsViewInput === 'moved')"
                    @click="setProductsView('moved')"
                >
                  Moved to Cleaned
                </button>
              </template>

              <!-- Cleaned tab: Active / Deleted / Archived -->
              <template v-if="currentTab === 'cleaned'">
                <button
                    type="button"
                    :class="segBtnClass(cleanedViewInput === 'active')"
                    @click="setCleanedView('active')"
                >
                  Active
                </button>

                <button
                    type="button"
                    :class="segBtnClass(cleanedViewInput === 'deleted')"
                    @click="setCleanedView('deleted')"
                >
                  Deleted
                </button>

                <button
                    type="button"
                    :class="segBtnClass(cleanedViewInput === 'archived')"
                    @click="setCleanedView('archived')"
                >
                  Archived
                </button>
              </template>
            </div>
          </div>
        </div>

        <!-- Tab Content -->
        <TabContent :currentTab="currentTab">
          <template v-slot:[currentTab]>
            <DataTableComponent
                :columns="tableColumns"
                @row-click="onRowClick"
                :key="`${currentTab}-${productsViewInput}-${cleanedViewInput}`"
                :rowClass="rowClass"
            >
              <template v-slot:action-slot="{ rowData }">
                <div class="flex items-center space-x-2 ml-4">

                  <!-- Deleted view: Restore only (sw editable only) -->
                  <template v-if="isDeletedView">
                    <template v-if="rowData.is_sw_editable">
                      <AppButton
                          v-if="currentTab === 'cleaned'"
                          color="green"
                          label=""
                          :icon="true"
                          @click.native.stop="restoreDeletedVerified(rowData.product_id)"
                          title="Restore"
                      >
                        <template v-slot:icon>
                          <Icon iconType="restore" />
                        </template>
                      </AppButton>

                      <AppButton
                          v-else
                          color="green"
                          label=""
                          :icon="true"
                          @click.native.stop="restoreDeletedOriginal(rowData.product_id)"
                          title="Restore"
                      >
                        <template v-slot:icon>
                          <Icon iconType="restore" />
                        </template>
                      </AppButton>
                    </template>
                  </template>

                  <!-- Normal view -->
                  <template v-else>
                    <!-- Edit: always visible for all products -->
                    <AppButton color="blue" label="" :icon="true" @click.native.stop="editProduct(rowData)">
                      <template v-slot:icon>
                        <Icon iconType="edit" />
                      </template>
                    </AppButton>

                    <!-- Copy / Delete: sw editable only -->
                    <template v-if="rowData.is_sw_editable">
                      <AppButton
                          v-if="currentTab === 'cleaned'"
                          color="blue"
                          label=""
                          :icon="true"
                          @click.native.stop="copyProductVerified(rowData.product_id)"
                      >
                        <template v-slot:icon>
                          <Icon iconType="copy-plus" />
                        </template>
                      </AppButton>

                      <AppButton
                          v-else
                          color="blue"
                          label=""
                          :icon="true"
                          @click.native.stop="copyProductOriginal(rowData.product_id)"
                      >
                        <template v-slot:icon>
                          <Icon iconType="copy-plus" />
                        </template>
                      </AppButton>

                      <AppButton
                          v-if="currentTab === 'cleaned'"
                          color="red"
                          label=""
                          :icon="true"
                          @click.native.stop="deleteProductVerified(rowData.product_id)"
                      >
                        <template v-slot:icon>
                          <Icon iconType="delete" />
                        </template>
                      </AppButton>

                      <AppButton
                          v-else
                          color="red"
                          label=""
                          :icon="true"
                          @click.native.stop="deleteProductOriginal(rowData.product_id)"
                      >
                        <template v-slot:icon>
                          <Icon iconType="delete" />
                        </template>
                      </AppButton>
                    </template>
                  </template>

                </div>
              </template>

              <template v-slot:description-slot="{ rowData }">
                <div class="truncate" style="max-width: 320px" :title="rowData.description || ''">
                  {{ rowData.description || '' }}
                </div>
              </template>

              <template v-slot:moved-to-verified-slot="{ rowData }">
                <div class="flex justify-center items-center h-full">
          <span v-if="rowData.moved_to_verified === true" class="text-green-600" title="Moved to Cleaned">
            <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
                width="22"
                height="22"
            >
              <path d="M20 6L9 17l-5-5"></path>
            </svg>
          </span>

                  <span v-else class="text-gray-400" title="Not moved">
            <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
                width="22"
                height="22"
            >
              <path d="M5 12h14"></path>
            </svg>
          </span>
                </div>
              </template>
            </DataTableComponent>
          </template>
        </TabContent>
      </div>
    </div>

    <!-- Create Product Panel -->
    <portal to="overlay-outlet">
      <panel :showing="createProductPanelOpen" @close="closeCreateProductPanel" title="Create New Product">
        <AddProduct />
      </panel>
    </portal>
  </div>
</template>

<script>
import PageHeader from "@/components/PageHeader.vue";
import TabHeaders from "@/components/TabHeaders.vue";
import TabContent from "@/components/TabContent.vue";
import DataTableComponent from "@/views/Products/Components/DataTableComponent.vue";
import Icon from "@/components/Icon.vue";
import AppButton from "@/components/Button.vue";
import Panel from "@/components/Panel.vue";
import AddProduct from "@/components/Products/AddProduct.vue";
import SearchTipsInfo from "@/components/AutoComplete/SearchTipsInfo.vue";
import { mapActions, mapState } from "vuex";
import notificationMixin from "@/mixins/notificationMixin";

export default {
  mixins: [notificationMixin],
  components: {
    PageHeader,
    TabHeaders,
    TabContent,
    DataTableComponent,
    Icon,
    AppButton,
    Panel,
    AddProduct,
    SearchTipsInfo,
  },
  data() {
    return {
      tabs: [
        { id: "products", label: "Products" },
        { id: "cleaned", label: "Cleaned (Verified)" },
      ],
      product_columns: [
        { field: "record_number", header: "Item Code", sortable: true, custom: false, width: "10%" },
        { field: "product_code", header: "Product Code", sortable: true, custom: false, width: "15%" },
        { field: "name", header: "Name", sortable: true, custom: false, width: "22%" },
        { field: "description", header: "Description", sortable: true, custom: true, slotName: "description-slot", width: "25%" },
        { field: "category", header: "Category", sortable: true, custom: false, width: "15%" },
        {
          field: "moved_to_verified",
          header: "Moved",
          sortable: true,
          custom: true,
          slotName: "moved-to-verified-slot",
          width: "6%",
          align: "center",
          headerClass: "column-h-text-center",
        },
        { field: "action", header: "", sortable: false, custom: true, slotName: "action-slot", width: "7%" },
      ],
      createProductPanelOpen: false,
    };
  },
  computed: {
    ...mapState("product", ["currentTab", "searchText", "productsView", "cleanedView"]),

    showViewFilters() {
      return this.currentTab === "products" || this.currentTab === "cleaned";
    },

    searchTextInput: {
      get() {
        return this.searchText;
      },
      set(value) {
        this.$store.commit("product/UPDATE_SEARCH_TEXT", value);
      },
    },

    productsViewInput: {
      get() {
        return this.productsView;
      },
      set(value) {
        this.$store.commit("product/UPDATE_PRODUCTS_VIEW", value);
      },
    },

    cleanedViewInput: {
      get() {
        return this.cleanedView;
      },
      set(value) {
        this.$store.commit("product/UPDATE_CLEANED_VIEW", value);
      },
    },

    isDeletedView() {
      return (
          (this.currentTab === "products" && this.productsViewInput === "deleted") ||
          (this.currentTab === "cleaned" && this.cleanedViewInput === "deleted")
      );
    },

    tableColumns() {
      const hideMoved = this.currentTab === "cleaned";

      return this.product_columns.filter((c) => {
        if (hideMoved && c.field === "moved_to_verified") return false;
        return true;
      });
    },
  },

  created() {
    if (!this.productsViewInput) this.$store.commit("product/UPDATE_PRODUCTS_VIEW", "active");
    if (!this.cleanedViewInput) this.$store.commit("product/UPDATE_CLEANED_VIEW", "active");
  },

  mounted() {
    this.fetchTableData();
  },

  methods: {
    ...mapActions("product", ["fetchTableData", "updateTab", "resetCurrentPage"]),

    segBtnClass(selected) {
      return [
        "px-3 py-1 text-sm rounded-md transition duration-200 focus:outline-none",
        selected ? "bg-white shadow border border-gray-200 text-secondary font-semibold" : "bg-transparent text-gray-600 hover:bg-white hover:shadow-sm",
      ].join(" ");
    },

    setProductsView(val) {
      this.$store.commit("product/UPDATE_PRODUCTS_VIEW", val);
      this.resetCurrentPage({ tab: this.currentTab, page: 0 });
      this.fetchTableData();
    },

    setCleanedView(val) {
      this.$store.commit("product/UPDATE_CLEANED_VIEW", val);
      this.resetCurrentPage({ tab: this.currentTab, page: 0 });
      this.fetchTableData();
    },

    async triggerSearch() {
      this.resetCurrentPage({ tab: this.currentTab, page: 0 });
      await this.fetchTableData();
    },

    onTabSelected(newTab) {
      const tabId = typeof newTab === "string" ? newTab : newTab.id;

      if (tabId === "products" && !this.productsViewInput) {
        this.$store.commit("product/UPDATE_PRODUCTS_VIEW", "active");
      }
      if (tabId === "cleaned" && !this.cleanedViewInput) {
        this.$store.commit("product/UPDATE_CLEANED_VIEW", "active");
      }

      this.resetCurrentPage({ tab: tabId, page: 0 });
      this.updateTab(tabId);
    },

    onRowClick(event) {
      if (this.isDeletedView) return;

      const productCode = (event.data.product_code || "").toLowerCase();
      this.$store.dispatch("setLastSelectedProduct", productCode);

      const routeName = this.currentTab === "cleaned" ? "product-verified-edit" : "product-edit";

      this.$router.push({
        name: routeName,
        params: { productCode },
      });
    },

    rowClass(rowData) {
      const last = (this.$store.state.lastSelectedProduct || "").toLowerCase();
      const code = (rowData.product_code || "").toLowerCase();
      return code && last && code === last ? "highlight-row" : "";
    },

    editProduct(product) {
      const productCode = (product.product_code || "").toLowerCase();
      this.$store.dispatch("setLastSelectedProduct", productCode);

      const routeName = this.currentTab === "cleaned" ? "product-verified-edit" : "product-edit";

      this.$router.push({
        name: routeName,
        params: { productCode },
      });
    },

    async copyProductOriginal(productId) {
      const confirmation = await this.confirmAction({
        message: "Do you want to copy this product?",
        header: "Confirm Action",
        acceptLabel: "Yes",
        rejectLabel: "No",
      });

      if (!confirmation) return;

      try {
        const newProduct = await this.ProductServiceV2.duplicateProduct(productId);
        this.notifySuccess("Product copied successfully");
        await this.fetchTableData();
        this.$store.dispatch("setLastSelectedProduct", (newProduct.product_code || "").toLowerCase());
      } catch (error) {
        const msg = error && error.data && error.data.message ? error.data.message : "Couldn't copy the product";
        this.notifyError(msg);
        console.error("Error copying product:", error);
      }
    },

    async copyProductVerified(productId) {
      const confirmation = await this.confirmAction({
        message: "Do you want to copy this product?",
        header: "Confirm Action",
        acceptLabel: "Yes",
        rejectLabel: "No",
      });

      if (!confirmation) return;

      try {
        const newProduct = await this.ProductVerifiedService.duplicateProduct(productId);
        this.notifySuccess("Product copied successfully");
        await this.fetchTableData();
        this.$store.dispatch("setLastSelectedProduct", (newProduct.product_code || "").toLowerCase());
      } catch (error) {
        const msg = error && error.data && error.data.message ? error.data.message : "Couldn't copy the product";
        this.notifyError(msg);
        console.error("Error copying product:", error);
      }
    },

    async deleteProductOriginal(productId) {
      const confirmation = await this.confirmAction({
        message: "Do you want to delete this product?",
        header: "Confirm Action",
        acceptLabel: "Yes",
        rejectLabel: "No",
      });

      if (!confirmation) return;

      try {
        await this.ProductServiceV2.deleteProduct(productId);
        this.notifySuccess("Product deleted successfully");
        await this.fetchTableData();
      } catch (error) {
        const msg = error && error.data && error.data.message ? error.data.message : "Couldn't delete the product";
        this.notifyError(msg);
        console.error("Error deleting product:", error);
      }
    },

    async deleteProductVerified(productId) {
      const confirmation = await this.confirmAction({
        message: "Do you want to delete this product?",
        header: "Confirm Action",
        acceptLabel: "Yes",
        rejectLabel: "No",
      });

      if (!confirmation) return;

      try {
        await this.ProductVerifiedService.deleteProduct(productId);
        this.notifySuccess("Product deleted successfully");
        await this.fetchTableData();
      } catch (error) {
        const msg = error && error.data && error.data.message ? error.data.message : "Couldn't delete the product";
        this.notifyError(msg);
        console.error("Error deleting product:", error);
      }
    },

    async restoreDeletedOriginal(productId) {
      const confirmation = await this.confirmAction({
        message: "Do you want to restore this product?",
        header: "Confirm Action",
        acceptLabel: "Yes",
        rejectLabel: "No",
      });

      if (!confirmation) return;

      try {
        await this.ProductServiceV2.restoreProduct(productId);
        this.notifySuccess("Product restored successfully");
        await this.fetchTableData();
      } catch (error) {
        const msg = error && error.data && error.data.message ? error.data.message : "Couldn't restore the product";
        this.notifyError(msg);
        console.error("Error restoring product:", error);
      }
    },

    async restoreDeletedVerified(productId) {
      const confirmation = await this.confirmAction({
        message: "Do you want to restore this product?",
        header: "Confirm Action",
        acceptLabel: "Yes",
        rejectLabel: "No",
      });

      if (!confirmation) return;

      try {
        await this.ProductVerifiedService.restoreProduct(productId);
        this.notifySuccess("Product restored successfully");
        await this.fetchTableData();
      } catch (error) {
        const msg = error && error.data && error.data.message ? error.data.message : "Couldn't restore the product";
        this.notifyError(msg);
        console.error("Error restoring product:", error);
      }
    },

    openCreateProductPanel() {
      this.createProductPanelOpen = true;
    },

    closeCreateProductPanel() {
      this.createProductPanelOpen = false;
    },
  },
};
</script>

<style scoped>
::v-deep .column-h-text-center .p-column-header-content {
  text-align: center;
  display: block !important;
}

::v-deep .highlight-row {
  background-color: rgba(0, 0, 0, 0.15) !important;
}
</style>